import { SocialOpportunitiesWidgetKind } from '@inference.engine/widgets/web';
import { ThumbnailImage } from '@/shared/ui/ThumbnailImage';
import { resolveWidgetThumbnailComponent, WidgetThumbnail } from '@/shared/ui/WidgetThumbnail';
import { ARTIFACT_KIND_PALETTE } from '../../../lib/artifact-kind-palette';
import type { WorkspaceArtifactRow } from '../ExistingArtifactsPicker.types';

interface Props {
  row: WorkspaceArtifactRow;
  /**
   * Widget render-data for this row, when a project-scoped host has resolved
   * it. Absent in the workspace-scoped picker (no project context to bulk-load
   * against), so widget kinds fall back to the palette icon there.
   */
  data?: Record<string, unknown> | null;
}

export function ThumbnailTile(props: Props) {
  const { row } = props;
  const palette = ARTIFACT_KIND_PALETTE[row.kind];
  const KindIcon = palette.icon;
  // image/video render a still poster; saved opportunities also carry a poster
  // (the deep-research thumbnail, signed into `thumbnailUrl` server-side) and
  // aren't registered for the live widget-thumbnail path above, so they use the
  // same plain <img>.
  const hasMedia =
    !!row.thumbnailUrl &&
    (row.kind === 'image' || row.kind === 'video' || row.kind === SocialOpportunitiesWidgetKind);

  const fallback = (
    <div className={`flex size-10 shrink-0 items-center justify-center rounded ${palette.bg}`}>
      <KindIcon className={`size-5 ${palette.text}`} />
    </div>
  );

  // Live widget preview for supported kinds, when render-data is available.
  // Matches the Library grid card path (shared resolver + thumbnail mode).
  // The thumbnail lays out at card size, so it would clip to blank in the 40px
  // tile — render it at a fixed source size and scale it down to fit.
  if (resolveWidgetThumbnailComponent(row.key) && props.data != null) {
    return (
      <div className="size-10 shrink-0 overflow-hidden rounded">
        <div className="h-[160px] w-[160px] origin-top-left scale-[0.25]">
          <WidgetThumbnail artifactKey={row.key} data={props.data} fallback={fallback} />
        </div>
      </div>
    );
  }

  // Both image and video tiles render a still PNG: images use their own asset,
  // videos use the server-extracted poster frame. Either way `thumbnailUrl` is
  // a static image, so an image covers both (no `<video>` partial GETs).
  if (hasMedia && row.thumbnailUrl) {
    return (
      <ThumbnailImage
        src={row.thumbnailUrl}
        alt={row.name}
        className="size-10 shrink-0 rounded"
        fallback={fallback}
      />
    );
  }
  return fallback;
}
